import { releaseListAPIMaxLimit } from '@gonfalon/dogfood-flags/dogfood-flags';
import {
  type GetAllReleasePipelinesQueryParams,
  getAllReleasePipelines,
} from '@gonfalon/openapi/operations/getAllReleasePipelines';
import {
  type GetAllReleaseProgressionsForReleasePipelineQueryParams,
  getAllReleaseProgressionsForReleasePipeline,
} from '@gonfalon/openapi/operations/getAllReleaseProgressionsForReleasePipeline';
import { getReleasePipelineByKey } from '@gonfalon/openapi/operations/getReleasePipelineByKey';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import { createQueryOptions } from '../internal/createQueryOptions';
import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';

export const getAllReleasePipelinesQuery = createQueryOptions(getAllReleasePipelines);

/**
 * @deprecated Use `createQueryOptions` with the OpenAPI operation directly instead.
 * See packages/rest-api/README.md for the recommended pattern.
 */
export const releasePipelines = createQueryKeys('release-pipelines', {
  list: ({ projectKey, params }: { projectKey: string; params?: GetAllReleasePipelinesQueryParams }) => ({
    queryKey: params ? [projectKey, params] : [projectKey],
    queryFn: async () => reactQueryResponseAdapter(getAllReleasePipelines({ projectKey, query: params })),
  }),
  detail: ({ projectKey, releasePipelineKey }: { projectKey: string; releasePipelineKey: string }) => ({
    queryKey: [projectKey, releasePipelineKey],
    queryFn: async ({ signal }) =>
      reactQueryResponseAdapter(getReleasePipelineByKey({ projectKey, pipelineKey: releasePipelineKey }, { signal })),
    contextQueries: {
      releaseProgressions: (props: { params?: GetAllReleaseProgressionsForReleasePipelineQueryParams } = {}) => {
        const { params } = props;
        return {
          // Working around some limitations of query-key-factory here.
          //
          // [params] | null is not a valid type for queryKey, so we add a
          // placeholder string ('releaseProgressions') to satisfy the types.
          queryKey: params ? ['releaseProgressions', params] : ['releaseProgressions'],
          queryFn: async () =>
            reactQueryResponseAdapter(
              getAllReleaseProgressionsForReleasePipeline({
                projectKey,
                pipelineKey: releasePipelineKey,
                query: params,
              }),
            ),
          contextQueries: {
            paginate: {
              queryKey: ['paginate'],
              queryFn: async (ctx) => {
                const { limit = releaseListAPIMaxLimit() } = params ?? {};
                const { pageParam: offset = 0 } = ctx as { pageParam?: number };
                return reactQueryResponseAdapter(
                  getAllReleaseProgressionsForReleasePipeline({
                    projectKey,
                    pipelineKey: releasePipelineKey,
                    query: { ...params, limit, offset },
                  }),
                );
              },
            },
          },
        };
      },
    },
  }),
});

export const releasePipelinesRoot = releasePipelines._def;
export const releasePipelinesList = releasePipelines.list;
export const releasePipelinesDetail = releasePipelines.detail;

export const releasePipelinesDetailReleaseProgressions = ({
  projectKey,
  releasePipelineKey,
  params = {},
}: {
  projectKey: string;
  releasePipelineKey: string;
  params?: GetAllReleaseProgressionsForReleasePipelineQueryParams;
}) => releasePipelines.detail({ projectKey, releasePipelineKey })._ctx.releaseProgressions({ params });
